import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { ForceExpandState } from '@/hooks/use-force-expand-state'
import { useForceExpandState } from '@/hooks/use-force-expand-state'
import type { ApproachComplexityEntry, VariableDefinition } from '../types'

interface ApproachComplexityEditorProps {
  complexities: ApproachComplexityEntry[]
  onUpdate: (complexities: ApproachComplexityEntry[]) => void
  /** Preferred function name from test config (raw slug form) */
  defaultFunctionName?: string
  forceExpandState?: ForceExpandState | null
}

export function ApproachComplexityEditor({
  complexities,
  onUpdate,
  defaultFunctionName,
  forceExpandState,
}: ApproachComplexityEditorProps) {
  const [isExpanded, setIsExpanded] = useForceExpandState(
    forceExpandState,
    false,
  )

  const hasAnyComplexity = complexities.some(
    (c) =>
      c.timeComplexity ||
      c.spaceComplexity ||
      c.timeComplexityExplanation ||
      c.spaceComplexityExplanation,
  )

  const handleAdd = () => {
    onUpdate([
      ...complexities,
      {
        functionName:
          complexities.length === 0 ? defaultFunctionName || 'main' : '',
        order: complexities.length,
      },
    ])
  }

  const handleRemove = (index: number) => {
    const updated = complexities
      .filter((_, i) => i !== index)
      .map((c, i) => ({ ...c, order: i }))
    onUpdate(updated)
  }

  const handleUpdateEntry = (
    index: number,
    updates: Partial<ApproachComplexityEntry>,
  ) => {
    const updated = complexities.map((c, i) =>
      i === index ? { ...c, ...updates } : c,
    )
    onUpdate(updated)
  }

  // Summary text for the collapsed header
  const summaryParts: string[] = []
  if (complexities.length > 0) {
    const first = complexities[0]
    if (first.timeComplexity) summaryParts.push(`Time: ${first.timeComplexity}`)
    if (first.spaceComplexity)
      summaryParts.push(`Space: ${first.spaceComplexity}`)
    if (complexities.length > 1)
      summaryParts.push(`+${complexities.length - 1} more`)
  }

  return (
    <div className="border-border border-t">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-muted/30 hover:bg-muted/50 flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <span className="font-medium">Complexity</span>
        {!isExpanded && hasAnyComplexity && (
          <span className="text-muted-foreground text-xs">
            {summaryParts.join(' · ')}
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="space-y-4 p-3">
          {complexities.map((entry, index) => (
            <ComplexityEntryEditor
              key={entry.id || index}
              entry={entry}
              index={index}
              onUpdate={(updates) => handleUpdateEntry(index, updates)}
              onRemove={() => handleRemove(index)}
              showRemove={complexities.length > 1}
            />
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAdd}
            className="w-full"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Function
          </Button>
        </div>
      )}
    </div>
  )
}

function ComplexityEntryEditor({
  entry,
  index,
  onUpdate,
  onRemove,
  showRemove,
}: {
  entry: ApproachComplexityEntry
  index: number
  onUpdate: (updates: Partial<ApproachComplexityEntry>) => void
  onRemove: () => void
  showRemove: boolean
}) {
  return (
    <div className="bg-muted/20 space-y-3 rounded-lg p-3">
      {/* Function name + remove */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <label className="text-muted-foreground mb-1 block text-xs font-medium">
            Function Name
          </label>
          <input
            type="text"
            value={entry.functionName}
            onChange={(e) => onUpdate({ functionName: e.target.value })}
            placeholder="e.g., solve, push, pop"
            className="border-input bg-background w-full rounded-md border px-2 py-1 font-mono text-sm"
          />
        </div>
        {showRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-muted-foreground hover:text-destructive mt-5 p-1"
            title="Remove complexity entry"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Time / Space complexity */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-muted-foreground mb-1 block text-xs font-medium">
            Time Complexity
          </label>
          <input
            type="text"
            value={entry.timeComplexity || ''}
            onChange={(e) =>
              onUpdate({ timeComplexity: e.target.value || null })
            }
            placeholder="e.g., O(n log n)"
            className="border-input bg-background w-full rounded-md border px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="text-muted-foreground mb-1 block text-xs font-medium">
            Space Complexity
          </label>
          <input
            type="text"
            value={entry.spaceComplexity || ''}
            onChange={(e) =>
              onUpdate({ spaceComplexity: e.target.value || null })
            }
            placeholder="e.g., O(n)"
            className="border-input bg-background w-full rounded-md border px-2 py-1 text-sm"
          />
        </div>
      </div>

      {/* Variable definitions */}
      <VariableDefinitionsEditor
        definitions={entry.variableDefinitions || []}
        onUpdate={(variableDefinitions) => onUpdate({ variableDefinitions })}
      />

      {/* Explanations */}
      <div>
        <label className="text-muted-foreground mb-1 block text-xs font-medium">
          Time Complexity Explanation
        </label>
        <textarea
          value={entry.timeComplexityExplanation || ''}
          onChange={(e) =>
            onUpdate({
              timeComplexityExplanation: e.target.value || null,
            })
          }
          placeholder="Explain the time complexity..."
          className="border-input bg-background min-h-[60px] w-full resize-y rounded-md border px-2 py-1 text-sm"
        />
      </div>
      <div>
        <label className="text-muted-foreground mb-1 block text-xs font-medium">
          Space Complexity Explanation
        </label>
        <textarea
          value={entry.spaceComplexityExplanation || ''}
          onChange={(e) =>
            onUpdate({
              spaceComplexityExplanation: e.target.value || null,
            })
          }
          placeholder="Explain the space complexity..."
          className="border-input bg-background min-h-[60px] w-full resize-y rounded-md border px-2 py-1 text-sm"
        />
      </div>
    </div>
  )
}

function VariableDefinitionsEditor({
  definitions,
  onUpdate,
}: {
  definitions: VariableDefinition[]
  onUpdate: (definitions: VariableDefinition[]) => void
}) {
  const handleAdd = () => {
    onUpdate([...definitions, { variable: '', definition: '' }])
  }

  const handleRemove = (index: number) => {
    onUpdate(definitions.filter((_, i) => i !== index))
  }

  const handleUpdateEntry = (
    index: number,
    field: keyof VariableDefinition,
    value: string,
  ) => {
    const updated = definitions.map((d, i) =>
      i === index ? { ...d, [field]: value } : d,
    )
    onUpdate(updated)
  }

  return (
    <div>
      <label className="text-muted-foreground mb-1 block text-xs font-medium">
        Variable Definitions
      </label>
      <div className="space-y-1.5">
        {definitions.map((def, index) => (
          <div
            key={`var-def-${def.variable || index}`}
            className="flex items-center gap-2"
          >
            <input
              type="text"
              value={def.variable}
              onChange={(e) =>
                handleUpdateEntry(index, 'variable', e.target.value)
              }
              placeholder="n"
              className="border-input bg-background w-16 rounded-md border px-2 py-1 font-mono text-sm"
            />
            <span className="text-muted-foreground text-sm">=</span>
            <input
              type="text"
              value={def.definition}
              onChange={(e) =>
                handleUpdateEntry(index, 'definition', e.target.value)
              }
              placeholder="number of nodes in the tree"
              className="border-input bg-background flex-1 rounded-md border px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="text-muted-foreground hover:text-destructive p-1"
              title="Remove variable"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={handleAdd}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs"
        >
          <Plus className="h-3 w-3" />
          Add variable
        </button>
      </div>
    </div>
  )
}
